import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/services/api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface Location {
  id: string;
  nombre: string;
}

export const useAccessibleLocations = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get locations from the locations endpoint
      const response = await authFetch(`${API_BASE_URL}/locations/all`);

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data && Array.isArray(result.data)) {
        // Backend returns locations filtered by employee_location_access table
        const accessibleLocations: Location[] = result.data.map((location: any) => ({
          id: location.id,
          nombre: location.nombre
        }));

        setLocations(accessibleLocations);
        console.log('🔍 DEBUG - User ID:', user?.id);
        console.log('🔍 DEBUG - Accessible locations from employee_location_access:', accessibleLocations);
      } else {
        console.error('Invalid locations response:', result);
        setError('Error loading locations');
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Error fetching locations');
    } finally {
      setLoading(false);
    }
  };

  // Get locations with "All locations" option if user has access to multiple locations
  const getLocationsWithAll = () => {
    // If user has access to multiple locations, show "All locations" option
    // This is now based on actual access from employee_location_access table
    if (locations.length > 1) {
      return [{ id: 'all', nombre: 'Todos los locales' }, ...locations];
    }

    return locations;
  };

  useEffect(() => {
    if (user) {
      fetchLocations();
    }
  }, [user]);

  return {
    locations,
    locationsWithAll: getLocationsWithAll(),
    loading,
    error,
    refetchLocations: fetchLocations
  };
};